import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="twofactor"
export default class extends Controller {

  static targets = [
    "trigger",
    "content",
    "sendsms",
    "verifysms",
    "phonenumber",
    "code",
    "currentPassword",
    "authenticatorCode",
    "authenticatorCurrentPassword",
    "authenticatorSuccess",
    "authenticatorError",
    "smsSuccess",
    "smsError",
    "recoveryCodes"    
  ]

  connect() {
  }

  // If the server demands step-up reauthentication, send the user to confirm
  // their password and report that we handled it.
  async reauthRedirected(response) {
    if (response.status !== 403) return false
    const data = await response.clone().json().catch(() => ({}))
    if (data.reauth_required && data.step_up_url) {
      window.location.href = data.step_up_url
      return true
    }
    return false
  }

  open(event) {

    event.preventDefault();
    this.contentTarget.classList.toggle("hidden");
    event.target.classList.add("opacity-50","pointer-events-none");

  }

  close(event) {

    event.preventDefault();
    this.contentTarget.classList.toggle("hidden");
    this.triggerTarget.classList.remove("opacity-50","pointer-events-none");

    // reset SMS
    if (this.hasSendsmsTarget) this.sendsmsTarget.classList.remove("hidden");
    if (this.hasVerifysmsTarget) this.verifysmsTarget.classList.add("hidden");

  }

  async viewRecoveryCodes(event) {
    event.preventDefault();
    const viewButton = event.target;
    viewButton.classList.add("opacity-50", "pointer-events-none");
    
    // Show loading state immediately
    this.recoveryCodesTarget.classList.remove("hidden");
  
    try {
      const response = await fetch('/two_factor_settings/recovery_codes', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name=csrf-token]').content,
        }
      });

      if (await this.reauthRedirected(response)) {
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch recovery codes');
      }
  
      const data = await response.json();
      this.recoveryCodesTarget.innerHTML = this.generateRecoveryCodesHtml(data.codes);
    } catch (error) {
      console.error('Error fetching recovery codes:', error);
      this.recoveryCodesTarget.innerHTML = `
        <div class="flex flex-col items-center justify-center space-y-3 py-4">
          <div class="text-error">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" class="text-red-600"><g clip-path="url(#a)"><path fill="currentColor" fill-rule="evenodd" d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16Zm0-3.8a.85.85 0 1 0 0-1.7.85.85 0 0 0 0 1.7Zm.75-7.7a.75.75 0 0 0-1.5 0v4a.75.75 0 0 0 1.5 0v-4Z" clip-rule="evenodd"/></g><defs><clipPath id="a"><path fill="#fff" d="M0 0h16v16H0z"/></clipPath></defs></svg>
          </div>
          <p class="text-sm text-error">Failed to load recovery codes. Please try again.</p>
        </div>
      `;
    } finally {
      viewButton.classList.remove("opacity-50", "pointer-events-none");
    }
  }  

  generateRecoveryCodesHtml(codes) {
    const codesList = codes.reduce((html, code, index, array) => {
      if (index % 2 === 0) {
        const code1 = code;
        const code2 = array[index + 1];
        return html + `
          <li class="inline-block w-[48%]"><code>${code1}</code></li>
          ${code2 ? `<li class="inline-block w-[48%]"><code>${code2}</code></li>` : ''}
        `;
      }
      return html;
    }, '');

    return `
      <p class="text-sm text-primary pb-4">These recovery keys are shown only once. Please copy and save them securely, as they cannot be viewed again here.</p>    
      <ul class="text-sm text-primary font-bold space-y-2">
        ${codesList}
      </ul>
      <p class="text-sm text-primary py-4" >Recovery keys are a backup method for logging into your account if you ever lose access to your primary two-factor authentication device (like an authenticator app or phone). Each code is a one-time password that can only be used once, so keep them safe and only use them in emergencies.</p>          
      <div class="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded-md text-sm">
        <p><strong>Important:</strong> If you lose access to your authenticator app or phone, these recovery keys are your only way back into your account.</p>
        <p><strong>Note:</strong> DoorFlow Support cannot restore access if two-factor credentials and recovery methods are lost.</p>
      </div>      
    `;
  }

  sendsms(event) {

    // send SMS here
    event.preventDefault();

    const areaCode = this.phonenumberTarget.querySelector('select').value;
    const phoneNumber = this.phonenumberTarget.querySelector('input').value;
    const data = { area_code: areaCode, telephone_number: phoneNumber };

    fetch('/two_factor_settings/sms', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name=csrf-token]').content,
      },
      body: JSON.stringify(data)
    })
    .then(response => {
      if (!response.ok) {
        // Parse the error response before throwing
        return response.json().then(err => {
          throw new Error(err.message || 'Something went wrong');
        });
      }
      return response;
    })
    .then(() => {
      // hide send button
      this.sendsmsTarget.classList.toggle("hidden");
      // enable verification form
      this.verifysmsTarget.classList.toggle("hidden");
    })
    .catch((error) => {
      alert("There was a problem: " + error.message);
    });

  }

  disableSms(event) {
    event.preventDefault();

    const data = {
      two_fa: {
        two_fa_method: 'sms'
      }
    }

    fetch('/two_factor_settings', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name=csrf-token]').content,
      },
      body: JSON.stringify(data)
    })
    .then(async response => {
      if (await this.reauthRedirected(response)) return;
      if (!response.ok) {
        throw Error(response.statusText);
      }
      return response.json();
    })
    .then(data => {
      if(data && data.status === 'success') {
        console.log(data.message);
        // Reload the page to reflect the changes
        location.reload();
      }
    })
    .catch((error) => {
      alert("There was a problem: " + error.message);
    });
  }

  disableAuthenticator(event) {
    event.preventDefault();

    const data = {
      two_fa: {
        two_fa_method: 'authenticator'
      }
    }

    fetch('/two_factor_settings', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name=csrf-token]').content,
      },
      body: JSON.stringify(data)
    })
    .then(async response => {
      if (await this.reauthRedirected(response)) return;
      if (!response.ok) {
        throw Error(response.statusText);
      }
      return response.json();
    })
    .then(data => {
      if(data && data.status === 'success') {
        // Reload the page to reflect the changes
        location.reload();
      }
    })
    .catch((error) => {
      alert("There was a problem: " + error.message);
    });
  }

  verifySmsCode(event) {
    event.preventDefault();

    const data = {
      two_fa: {
        code: this.codeTarget.value,
        password: this.currentPasswordTarget.value,
        area_code: this.phonenumberTarget.querySelector('select').value,
        telephone_number: this.phonenumberTarget.querySelector('input').value,
        two_fa_method: 'sms'
      }
    };

    this.verifyCode(data, this.smsSuccessTarget, this.smsErrorTarget);
  }

  verifyAuthenticatorCode(event) {
    event.preventDefault();
    console.log(this.authenticatorCodeTarget)
    const data = {
      two_fa: {
        code: this.authenticatorCodeTarget.value,
        password: this.authenticatorCurrentPasswordTarget.value,
        two_fa_method: 'authenticator'
      }
    };

    this.verifyCode(data, this.authenticatorSuccessTarget, this.authenticatorErrorTarget);
  }

  verifyCode(data, successTarget, errorTarget) {
    fetch('/two_factor_settings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name=csrf-token]').content,
      },
      body: JSON.stringify(data)
    })
    .then(response => response.json())
    .then(data => {
      if (data.status === "success") {
        // Reload the page to reflect the changes
        location.reload();
      } else {
        // Display the error message
        errorTarget.classList.remove('hidden');
        errorTarget.textContent = data.message;

        // Hide the success message if it was previously displayed
        successTarget.classList.add('hidden');
      }
    })
    .catch((error) => {
      // Display the error message
      errorTarget.classList.remove('hidden');
      errorTarget.textContent = error.message;

      // Hide the success message if it was previously displayed
      successTarget.classList.add('hidden');
    });
  }
}
